import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';

// --- STRIPE CONNECT EMBEDDED IMPORTS ---
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { loadConnectAndInitialize } from '@stripe/connect-js';

// --- NEW STRIPE CHECKOUT EMBEDDED IMPORTS ---
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export default function FTLOnboarding({ onComplete }) {
  const { currentUser } = useAuth();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [tenantData, setTenantData] = useState(null);

  // --- DYNAMIC ROUTING STATE ---
  const [step, setStep] = useState(1);
  const [activeSteps, setActiveSteps] = useState([1, 2, 3, 5]); // Default W2 Flow

  // --- STRIPE CONNECT STATE ---
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [isConnectLoading, setIsConnectLoading] = useState(false);

  // --- STRIPE CHECKOUT STATE ---
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);

  // --- WIZARD STATE ---
  const [gearData, setGearData] = useState({ preferredName: '', shirtSize: '', jacketSize: '' });
  const [financialData, setFinancialData] = useState({ ssn: '', routingNumber: '', accountNumber: '' });
  
  // Federal Tax Gateway (1099)
  const [taxData, setTaxData] = useState({ legalBusinessName: '', classification: 'Individual/Sole Proprietor' });
  
  // Compliance Matrix State
  const [complianceData, setComplianceData] = useState({ 
    handbook: false, pfml: false, // W2
    ica: false, nda: false, techLicense: false, autonomy: false, azDibs: false // 1099 & Enterprise
  });

  // Secure Document Vault
  const [uploadData, setUploadData] = useState({ coiFile: null, coiUrl: '', entityFile: null, entityUrl: '', isUploading: false });

  const [signatureData, setSignatureData] = useState({ typedName: '', fontStyle: 'font-["Brush_Script_MT",_cursive]' });

  // Fetch User Profile & Initialize Dynamic Flow
  useEffect(() => {
    async function fetchUserData() {
      if (!currentUser) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserProfile(data);

          // Fetch associated tenant data for billing calculations
          if (data.tenant_id) {
            const tenantSnap = await getDoc(doc(db, 'clients', data.tenant_id));
            if (tenantSnap.exists()) setTenantData(tenantSnap.data());
          }
          
          const isOwner = ['TENANT_ADMIN', 'MASTER_AGENT', 'CLIENT_OWNER', 'CLIENT'].includes(data.accountType);
          const isContr = data.accountType === 'CONTRACTOR';

          if (isOwner) {
            // Enterprise Flow: Fin/Tax -> Compliance -> Vault -> Signature -> Connect Routing (Step 6) -> Billing Gateway (Step 7)
            setActiveSteps([2, 3, 4, 5, 6, 7]); 
            setStep(2);
          } else if (isContr) {
            // Standard 1099 Flow
            setActiveSteps([2, 3, 4, 5]); 
            setStep(2);
          }
        }
      } catch (err) {
        console.error("FTL Verification Error:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchUserData();
  }, [currentUser]);

  // --- STRIPE CONNECT INITIALIZATION EFFECT (Triggers on Step 6) ---
  useEffect(() => {
    let isMounted = true;
    const isTenantOwner = ['CLIENT', 'MASTER_AGENT', 'CLIENT_OWNER', 'TENANT_ADMIN'].includes(userProfile?.accountType);

    if (step === 6 && isTenantOwner && !stripeConnectInstance) {
      const initConnect = async () => {
        setIsConnectLoading(true);
        try {
          const generateSession = httpsCallable(functions, 'generateConnectSession');
          const res = await generateSession({ 
            tenantId: userProfile.tenant_id
          });

          if (res.data.clientSecret && isMounted) {
            const instance = loadConnectAndInitialize({
              publishableKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
              fetchClientSecret: async () => res.data.clientSecret,
              appearance: {
                overlays: 'dialog',
                variables: {
                  colorPrimary: '#10b981', // Matches eeKo FTL Emerald Green
                  colorBackground: '#ffffff',
                },
              },
            });
            setStripeConnectInstance(instance);
          }
        } catch (err) {
          console.error("Failed to init Stripe Connect:", err);
        } finally {
          if (isMounted) setIsConnectLoading(false);
        }
      };
      initConnect();
    }
    return () => { isMounted = false; };
  }, [step, userProfile, stripeConnectInstance]);

  const is1099OrOwner = ['CONTRACTOR', 'CLIENT', 'MASTER_AGENT', 'CLIENT_OWNER', 'TENANT_ADMIN'].includes(userProfile?.accountType);
  const isTenantOwner = ['CLIENT', 'MASTER_AGENT', 'CLIENT_OWNER', 'TENANT_ADMIN'].includes(userProfile?.accountType);
  const operatingState = (userProfile?.operatingState || 'WA').toUpperCase();
  const offlineDocs = userProfile?.offlineDocs || {};

  // --- THE PLG ENTERPRISE PRICING STACK ---
  const BASE_DOOR_FEE = tenantData?.negotiatedRates?.door_fee || 249;
  const CHECKHQ_FEE = tenantData?.negotiatedRates?.payroll_base || 29;
  const SEAT_FEE = 5;
  const TOTAL_MRR = BASE_DOOR_FEE + CHECKHQ_FEE + SEAT_FEE;
  const SETUP_FEE = tenantData?.setupFee || tenantData?.calculatedOneTime || 0;

  // --- DYNAMIC COMPLIANCE MATRIX ENGINE ---
  const getRequiredAgreements = () => {
    if (!is1099OrOwner) {
      return operatingState === 'WA' ? ['handbook', 'pfml'] : ['handbook'];
    }
    const reqs = ['ica', 'nda', 'techLicense', 'autonomy'];
    if (operatingState === 'AZ') reqs.push('azDibs');
    return reqs;
  };

  const requiredAgreements = getRequiredAgreements();
  const isComplianceSkipped = is1099OrOwner && offlineDocs.ica && offlineDocs.nda;

  const currentStepIndex = activeSteps.indexOf(step);
  const handleNext = () => setStep(activeSteps[currentStepIndex + 1]);
  const handleBack = () => {
    setCheckoutClientSecret(null); // Reset checkout if going back
    setStep(activeSteps[currentStepIndex - 1]);
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadData(prev => ({ ...prev, isUploading: true }));
    try {
      const fileExt = file.name.split('.').pop();
      const storageRef = ref(storage, `compliance_vault/${currentUser.uid}/${type}_${Date.now()}.${fileExt}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      if (type === 'coi') setUploadData(prev => ({ ...prev, coiFile: file, coiUrl: url }));
      if (type === 'entity') setUploadData(prev => ({ ...prev, entityFile: file, entityUrl: url }));
    } catch (error) {
      console.error("Secure upload failed:", error);
      alert("Failed to securely vault document. Please try again.");
    } finally {
      setUploadData(prev => ({ ...prev, isUploading: false }));
    }
  };

  const executeSwitchFlip = async () => {
    if (!signatureData.typedName) return alert('Legal signature required.');
    setIsSubmitting(true);

    try {
      // 1. Compile Array of Agreements to Sign
      const agreementsToSign = is1099OrOwner 
        ? requiredAgreements.filter(req => !offlineDocs[req]) 
        : [];

      let vaultedUrls = {};
      let auditId = null;

      // 2. Call the Secure Backend Stamping Engine
      if (agreementsToSign.length > 0) {
        const compileAndVault = httpsCallable(functions, 'compileAndVaultAgreements');
        const vaultRes = await compileAndVault({
          uid: currentUser.uid,
          tenantId: userProfile.tenant_id || 'corp_reinhardt',
          signatureName: signatureData.typedName,
          fontStyle: signatureData.fontStyle, 
          agreements: agreementsToSign
        });
        
        vaultedUrls = vaultRes.data.vaultedUrls;
        auditId = vaultRes.data.auditId;
      }

      // 3. Update Firestore with the Official Vault URLs
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        ftlCompleted: !isTenantOwner, 
        status: isTenantOwner ? 'pending_billing' : 'Active',
        complianceStatus: 'verified',
        secureVault: {
          gear: is1099OrOwner ? null : gearData,
          financial: financialData,
          taxIdentity: is1099OrOwner ? taxData : null, 
          legal: {
            handbookAcknowledged: is1099OrOwner ? null : complianceData.handbook,
            pfmlAcknowledged: is1099OrOwner ? null : complianceData.pfml,
            icaAcknowledged: offlineDocs.ica ? 'verified_offline' : (vaultedUrls.ica || null),
            ndaAcknowledged: offlineDocs.nda ? 'verified_offline' : (vaultedUrls.nda || null),
            techLicenseAcknowledged: vaultedUrls.techLicense || null,
            autonomyAcknowledged: vaultedUrls.autonomy || null,
            azDibsAcknowledged: operatingState === 'AZ' ? (vaultedUrls.azDibs || null) : null,
            signature: {
              name: signatureData.typedName,
              font: signatureData.fontStyle,
              auditId: auditId,
              timestamp: serverTimestamp(),
              userAgent: navigator.userAgent
            }
          },
          assets: is1099OrOwner ? {
            coiDocumentUrl: uploadData.coiUrl,
            entityDocumentUrl: uploadData.entityUrl || null
          } : null
        },
        updatedAt: serverTimestamp()
      });

      if (isTenantOwner) {
        setStep(6); 
        setIsSubmitting(false);
      } else {
        if (onComplete) onComplete();
      }

    } catch (error) {
      console.error('FTL Execution Error:', error);
      alert('Failed to execute secure vault sequence. Please contact corporate.');
      setIsSubmitting(false);
    }
  };

  // --- STRIPE EMBEDDED CHECKOUT EXECUTION ---
  const launchStripeCheckout = async () => {
    setIsSubmitting(true);
    try {
      const createCheckout = httpsCallable(functions, 'createCheckoutSession');
      const res = await createCheckout({
        dealerName: tenantData?.companyName || userProfile?.companyName || 'Enterprise Client',
        email: currentUser.email,
        setupFee: SETUP_FEE,
        mrr: TOTAL_MRR,
        dealerId: userProfile.tenant_id,
        customItemName: 'Enterprise Network Implementation',
        subscriptionItemName: 'Apex OS Enterprise Stack (Bundled)',
        // The return URL is required by Stripe Embedded to redirect the iframe back to a clean state after payment
        returnUrl: window.location.origin + '/dashboard?session_id={CHECKOUT_SESSION_ID}'
      });
      
      // Store the secret, which will trigger the UI to render the <EmbeddedCheckoutProvider>
      setCheckoutClientSecret(res.data.clientSecret);
      setIsSubmitting(false);
    } catch (err) {
      console.error("Stripe Launch Failed:", err);
      alert('Billing Gateway Error: ' + err.message);
      setIsSubmitting(false);
    }
  };

  const renderProgress = () => (
    <div className="flex items-center justify-between mb-8 relative">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 z-0 rounded"></div>
      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-emerald-500 z-0 rounded transition-all duration-500" style={{ width: `${(currentStepIndex / (activeSteps.length - 1)) * 100}%` }}></div>
      
      {[1, 2, 3, 4, 5, 6, 7].filter(n => activeSteps.includes(n)).map((num) => {
        return (
          <div key={num} className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs z-10 border-2 transition-colors duration-300 ${activeSteps.indexOf(num) <= currentStepIndex ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-100 border-slate-300 text-slate-400'}`}>
            {activeSteps.indexOf(num) < currentStepIndex ? <i className="fa-solid fa-check"></i> : (activeSteps.indexOf(num) + 1)}
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    return <div className="fixed inset-0 z-[100] bg-slate-900 flex items-center justify-center"><div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  const requiresW9Only = is1099OrOwner && offlineDocs.w9 === false;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900 flex items-center justify-center p-4 sm:p-8 overflow-y-auto animate-in fade-in duration-500 font-sans">
      <div className="w-full max-w-2xl bg-white rounded shadow-2xl overflow-hidden flex flex-col my-auto">
        
        <div className="bg-slate-900 p-8 text-center text-white shrink-0 border-b border-slate-800">
          <div className="w-16 h-16 bg-slate-800 border border-slate-700 flex items-center justify-center rounded-full mx-auto mb-4 text-emerald-500">
            <i className="fa-solid fa-shield-halved text-2xl"></i>
          </div>
          <h1 className="text-2xl font-black uppercase tracking-widest">Enterprise Onboarding</h1>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">First Time Login (FTL) Sequence</p>
        </div>

        <div className="p-8 flex-1">
          {renderProgress()}

          {/* STEP 1: GEAR (W2 ONLY) */}
          {step === 1 && !is1099OrOwner && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Uniform & Gear Provisioning</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Let's get your store apparel ordered.</p>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Preferred First Name <span className="text-red-500">*</span></label>
                  <input type="text" value={gearData.preferredName} onChange={(e) => setGearData({...gearData, preferredName: e.target.value})} className="w-full bg-slate-50 border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" placeholder="e.g., Matt" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Shirt Size <span className="text-red-500">*</span></label>
                    <select value={gearData.shirtSize} onChange={(e) => setGearData({...gearData, shirtSize: e.target.value})} className="w-full bg-slate-50 border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 cursor-pointer rounded-sm">
                      <option value="">Select Size...</option><option value="S">Small</option><option value="M">Medium</option><option value="L">Large</option><option value="XL">X-Large</option><option value="XXL">XX-Large</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Jacket Size <span className="text-red-500">*</span></label>
                    <select value={gearData.jacketSize} onChange={(e) => setGearData({...gearData, jacketSize: e.target.value})} className="w-full bg-slate-50 border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 cursor-pointer rounded-sm">
                      <option value="">Select Size...</option><option value="S">Small</option><option value="M">Medium</option><option value="L">Large</option><option value="XL">X-Large</option><option value="XXL">XX-Large</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: FEDERAL TAX & FINANCIAL ROUTING */}
          {step === 2 && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Tax & Financial Routing</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Encrypted payload directly to the enterprise vault.</p>
              
              <div className="space-y-5 bg-slate-50 p-6 border border-slate-200 rounded-sm shadow-inner">
                
                {is1099OrOwner && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-slate-200">
                      <div className="col-span-1 md:col-span-2">
                        <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-2">Form W-9 Tax Certification</p>
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Legal Business Name <span className="text-red-500">*</span></label>
                        <input type="text" value={taxData.legalBusinessName} onChange={(e) => setTaxData({...taxData, legalBusinessName: e.target.value})} placeholder="As shown on income tax return" className="w-full bg-white border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Federal Tax Classification <span className="text-red-500">*</span></label>
                        <select value={taxData.classification} onChange={(e) => setTaxData({...taxData, classification: e.target.value})} className="w-full bg-white border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 cursor-pointer rounded-sm">
                          <option value="Individual/Sole Proprietor">Individual / Sole Proprietor</option>
                          <option value="C Corporation">C Corporation</option>
                          <option value="S Corporation">S Corporation</option>
                          <option value="Partnership">Partnership</option>
                          <option value="LLC">Limited Liability Company (LLC)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">TIN / EIN / SSN <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                          <input type="password" value={financialData.ssn} onChange={(e) => setFinancialData({...financialData, ssn: e.target.value})} placeholder="XX-XXXXXXX" className="w-full bg-white border border-slate-300 pl-10 pr-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* W2 Only SSN */}
                {!is1099OrOwner && (
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Social Security Number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                      <input type="password" value={financialData.ssn} onChange={(e) => setFinancialData({...financialData, ssn: e.target.value})} placeholder="XXX-XX-XXXX" className="w-full bg-white border border-slate-300 pl-10 pr-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="col-span-1 md:col-span-2">
                    <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-2">Direct Deposit Authorization</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Bank Routing Number <span className="text-red-500">*</span></label>
                    <input type="text" maxLength="9" value={financialData.routingNumber} onChange={(e) => setFinancialData({...financialData, routingNumber: e.target.value})} placeholder="9 Digits" className="w-full bg-white border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Bank Account Number <span className="text-red-500">*</span></label>
                    <input type="password" value={financialData.accountNumber} onChange={(e) => setFinancialData({...financialData, accountNumber: e.target.value})} placeholder="Account #" className="w-full bg-white border border-slate-300 px-4 py-3 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: DYNAMIC COMPLIANCE HANDOFF */}
          {step === 3 && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Legal & Compliance</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Review the corporate policies.</p>

              {isComplianceSkipped ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 text-center rounded-sm">
                  <i className="fa-solid fa-file-shield text-3xl text-emerald-500 mb-3"></i>
                  <h3 className="text-sm font-black text-emerald-900 uppercase tracking-widest">Compliance Verified</h3>
                  <p className="text-xs text-emerald-700 font-medium mt-1">Your legal agreements have already been collected and verified offline by administration. You may proceed.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* W2 Docs */}
                  {!is1099OrOwner && (
                    <>
                      {requiredAgreements.includes('handbook') && (
                        <div className={`p-5 border rounded-sm transition-colors ${complianceData.handbook ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start gap-4">
                            <input type="checkbox" checked={complianceData.handbook} onChange={(e) => setComplianceData({...complianceData, handbook: e.target.checked})} className="mt-0.5 w-5 h-5 cursor-pointer accent-emerald-600" />
                            <div>
                              <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest">Employee Handbook</h4>
                              <p className="text-[10px] font-medium text-slate-500 mt-1 mb-3">Acknowledge corporate policies.</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {requiredAgreements.includes('pfml') && (
                        <div className={`p-5 border rounded-sm transition-colors ${complianceData.pfml ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start gap-4">
                            <input type="checkbox" checked={complianceData.pfml} onChange={(e) => setComplianceData({...complianceData, pfml: e.target.checked})} className="mt-0.5 w-5 h-5 cursor-pointer accent-emerald-600" />
                            <div>
                              <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest">WA PFML Notices</h4>
                              <p className="text-[10px] font-medium text-slate-500 mt-1 mb-3">Acknowledge state leave notices.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* 1099 / Enterprise Docs */}
                  {is1099OrOwner && (
                    <>
                      {!offlineDocs.ica && requiredAgreements.includes('ica') && (
                        <div className={`p-4 border rounded-sm transition-colors ${complianceData.ica ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <label className="flex items-start gap-4 cursor-pointer flex-1">
                              <input type="checkbox" checked={complianceData.ica} onChange={(e) => setComplianceData({...complianceData, ica: e.target.checked})} className="mt-0.5 w-5 h-5 accent-emerald-600 shrink-0" />
                              <div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Master Services Agreement (MSA)</h4>
                                <p className="text-[10px] font-medium text-slate-500 mt-1">Execute standard enterprise terms, billing liabilities, and SLA agreements.</p>
                              </div>
                            </label>
                            <a href="https://storage.googleapis.com/apex-retail-suite.firebasestorage.app/INDEPENDENT%20CONTRACTOR%20AGREEMENT_RSaaST.pdf" target="_blank" rel="noopener noreferrer" className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                              Review Document
                            </a>
                          </div>
                        </div>
                      )}
                      {!offlineDocs.nda && requiredAgreements.includes('nda') && (
                        <div className={`p-4 border rounded-sm transition-colors ${complianceData.nda ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <label className="flex items-start gap-4 cursor-pointer flex-1">
                              <input type="checkbox" checked={complianceData.nda} onChange={(e) => setComplianceData({...complianceData, nda: e.target.checked})} className="mt-0.5 w-5 h-5 accent-emerald-600 shrink-0" />
                              <div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Non-Disclosure Agreement</h4>
                                <p className="text-[10px] font-medium text-slate-500 mt-1">Protect corporate trade secrets, operations, and IP.</p>
                              </div>
                            </label>
                            <a href="https://storage.googleapis.com/apex-retail-suite.firebasestorage.app/MUTUAL%20NON-DISCLOSURE%20%26%20CONFIDENTIALITY%20AGREEMENT_RSaaST.pdf" target="_blank" rel="noopener noreferrer" className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                              Review Document
                            </a>
                          </div>
                        </div>
                      )}
                      {requiredAgreements.includes('techLicense') && (
                        <div className={`p-4 border rounded-sm transition-colors ${complianceData.techLicense ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <label className="flex items-start gap-4 cursor-pointer flex-1">
                              <input type="checkbox" checked={complianceData.techLicense} onChange={(e) => setComplianceData({...complianceData, techLicense: e.target.checked})} className="mt-0.5 w-5 h-5 accent-emerald-600 shrink-0" />
                              <div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Technology Licensing Acknowledgment</h4>
                                <p className="text-[10px] font-medium text-slate-500 mt-1">I agree I am accessing this system under a corporate license and Reinhardt SaaS Tech does not provide physical business tools.</p>
                              </div>
                            </label>
                            <a href="https://storage.googleapis.com/apex-retail-suite.firebasestorage.app/TECHNOLOGY%20LICENSING%20%26%20HARDWARE%20ACKNOWLEDGMENT_RSaaST.pdf" target="_blank" rel="noopener noreferrer" className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                              Review Document
                            </a>
                          </div>
                        </div>
                      )}
                      {requiredAgreements.includes('autonomy') && (
                        <div className={`p-4 border rounded-sm transition-colors ${complianceData.autonomy ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-300'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <label className="flex items-start gap-4 cursor-pointer flex-1">
                              <input type="checkbox" checked={complianceData.autonomy} onChange={(e) => setComplianceData({...complianceData, autonomy: e.target.checked})} className="mt-0.5 w-5 h-5 accent-emerald-600 shrink-0" />
                              <div>
                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Operational Autonomy Clause</h4>
                                <p className="text-[10px] font-medium text-slate-500 mt-1">I confirm I am an independent entity and hold sole responsibility for my retail and staffing operations.</p>
                              </div>
                            </label>
                            <a href="https://storage.googleapis.com/apex-retail-suite.firebasestorage.app/OPERATIONAL%20AUTONOMY%20%26%20DISCRETION%20ACKNOWLEDGMENT.pdf" target="_blank" rel="noopener noreferrer" className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                              Review Document
                            </a>
                          </div>
                        </div>
                      )}
                      {requiredAgreements.includes('azDibs') && (
                        <div className={`p-4 border-2 rounded-sm transition-colors ${complianceData.azDibs ? 'bg-amber-50 border-amber-400' : 'bg-white border-amber-200'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <label className="flex items-start gap-4 cursor-pointer flex-1">
                              <input type="checkbox" checked={complianceData.azDibs} onChange={(e) => setComplianceData({...complianceData, azDibs: e.target.checked})} className="mt-0.5 w-5 h-5 accent-amber-600 shrink-0" />
                              <div>
                                <h4 className="text-xs font-black text-amber-900 uppercase tracking-widest">Arizona Declaration of Independent Business Status</h4>
                                <p className="text-[10px] font-medium text-amber-700 mt-1">Pursuant to A.R.S. § 23-902, I acknowledge I am an independent contractor operating a separate business entity and am not entitled to workers' compensation benefits.</p>
                              </div>
                            </label>
                            <a href="https://storage.googleapis.com/apex-retail-suite.firebasestorage.app/ARIZONA%20DIBS_RSaaST.pdf" target="_blank" rel="noopener noreferrer" className="shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold px-3 py-1.5 uppercase tracking-widest transition-colors shadow-sm rounded-sm">
                              Review Document
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: DOCUMENT VAULT (1099/ENTERPRISE ONLY) */}
          {step === 4 && is1099OrOwner && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Corporate Asset Vault</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Upload required business verifications.</p>

              <div className="space-y-6">
                <div className="bg-slate-50 border border-slate-300 p-5 rounded-sm">
                  <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">Certificate of Insurance (COI) <span className="text-red-500">*</span></h4>
                  <p className="text-[10px] text-slate-500 font-medium mb-4">Provide proof of general liability insurance to establish business separation.</p>
                  
                  {uploadData.coiUrl ? (
                    <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 p-3 border border-emerald-200 rounded-sm">
                      <i className="fa-solid fa-file-circle-check text-xl"></i>
                      <span className="text-xs font-bold uppercase tracking-widest">{uploadData.coiFile.name}</span>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-sm cursor-pointer hover:bg-slate-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <i className="fa-solid fa-cloud-arrow-up text-slate-400 text-xl mb-2"></i>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Click to upload PDF</p>
                      </div>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e, 'coi')} disabled={uploadData.isUploading} />
                    </label>
                  )}
                </div>

                <div className="bg-slate-50 border border-slate-300 p-5 rounded-sm">
                  <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">Proof of Business Entity <span className="text-slate-400 font-normal ml-2">(Optional)</span></h4>
                  <p className="text-[10px] text-slate-500 font-medium mb-4">Upload your LLC filing or business license to strengthen classification defense.</p>
                  
                  {uploadData.entityUrl ? (
                    <div className="flex items-center gap-3 text-emerald-600 bg-emerald-50 p-3 border border-emerald-200 rounded-sm">
                      <i className="fa-solid fa-file-circle-check text-xl"></i>
                      <span className="text-xs font-bold uppercase tracking-widest">{uploadData.entityFile.name}</span>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-sm cursor-pointer hover:bg-slate-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <i className="fa-solid fa-cloud-arrow-up text-slate-400 text-xl mb-2"></i>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Click to upload PDF</p>
                      </div>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e, 'entity')} disabled={uploadData.isUploading} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: DIGITAL SIGNATURE */}
          {step === 5 && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Execution & Electronic Signature</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Per the U.S. ESIGN Act of 2000, your typed signature is legally binding.</p>

              <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Type Your Full Legal Name <span className="text-red-500">*</span></label>
                <input type="text" value={signatureData.typedName} onChange={(e) => setSignatureData({...signatureData, typedName: e.target.value})} className="w-full bg-white border border-slate-300 px-4 py-3 text-lg font-medium text-slate-900 focus:outline-none focus:border-emerald-500 rounded-sm mb-6" placeholder="Jane Doe" />

                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Select Signature Style</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                  <div onClick={() => setSignatureData({...signatureData, fontStyle: 'font-["Brush_Script_MT",_cursive]'})} className={`border p-4 flex items-center justify-center cursor-pointer transition-colors rounded-sm h-20 ${signatureData.fontStyle === 'font-["Brush_Script_MT",_cursive]' ? 'bg-white border-emerald-500 shadow-md' : 'bg-slate-100 border-slate-200 hover:border-slate-300'}`}>
                    <span className={`text-2xl font-["Brush_Script_MT",_cursive] ${signatureData.typedName ? 'text-slate-900' : 'text-slate-400'}`}>{signatureData.typedName || 'Jane Doe'}</span>
                  </div>
                  <div onClick={() => setSignatureData({...signatureData, fontStyle: 'font-["Bradley_Hand",_cursive]'})} className={`border p-4 flex items-center justify-center cursor-pointer transition-colors rounded-sm h-20 ${signatureData.fontStyle === 'font-["Bradley_Hand",_cursive]' ? 'bg-white border-emerald-500 shadow-md' : 'bg-slate-100 border-slate-200 hover:border-slate-300'}`}>
                    <span className={`text-2xl font-["Bradley_Hand",_cursive] ${signatureData.typedName ? 'text-slate-900' : 'text-slate-400'}`}>{signatureData.typedName || 'Jane Doe'}</span>
                  </div>
                  <div onClick={() => setSignatureData({...signatureData, fontStyle: 'font-serif italic'})} className={`border p-4 flex items-center justify-center cursor-pointer transition-colors rounded-sm h-20 ${signatureData.fontStyle === 'font-serif italic' ? 'bg-white border-emerald-500 shadow-md' : 'bg-slate-100 border-slate-200 hover:border-slate-300'}`}>
                    <span className={`text-xl font-serif italic ${signatureData.typedName ? 'text-slate-900' : 'text-slate-400'}`}>{signatureData.typedName || 'Jane Doe'}</span>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm flex items-start gap-3">
                  <i className="fa-solid fa-scale-balanced text-emerald-600 mt-1"></i>
                  <p className="text-[9px] font-bold text-emerald-800 leading-relaxed uppercase tracking-widest">
                    By confirming below, you agree that the signature above constitutes your electronic signature. Your IP address and the server timestamp will be permanently vaulted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 6: STRIPE CONNECT EMBEDDED ROUTING */}
          {step === 6 && isTenantOwner && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Configure Retail Payouts</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Link your bank account to receive daily deposits from your physical retail stores.</p>

              <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden mb-6 min-h-[450px] relative">
                 {isConnectLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
                       <i className="fa-solid fa-spinner fa-spin text-3xl text-emerald-500 mb-4"></i>
                       <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Securing Banking Tunnel...</p>
                    </div>
                 )}
                 
                 {stripeConnectInstance ? (
                   <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                     {/* 
                       Stripe handles the entire KYC and bank linkage form here.
                       When they hit "Submit" or "Exit" inside the iframe, it advances them to Step 7.
                     */}
                     <ConnectAccountOnboarding 
                       onExit={() => handleNext()} 
                     />
                   </ConnectComponentsProvider>
                 ) : (
                   !isConnectLoading && (
                      <div className="p-8 text-center flex flex-col items-center justify-center h-full">
                         <i className="fa-solid fa-triangle-exclamation text-rose-500 text-3xl mb-3"></i>
                         <p className="text-sm font-bold text-rose-600">Failed to load secure banking component.</p>
                         <button onClick={handleNext} className="mt-6 text-xs font-bold text-slate-500 underline uppercase tracking-widest hover:text-slate-800">Skip for now</button>
                      </div>
                   )
                 )}
              </div>
            </div>
          )}

          {/* STEP 7: STRIPE CHECKOUT EMBEDDED */}
          {step === 7 && isTenantOwner && (
            <div className="animate-in slide-in-from-right-4 duration-300">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-1">Enterprise Subscription Billing</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Finalize your account to unlock the workspace.</p>

              {checkoutClientSecret ? (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden mb-6 min-h-[500px]">
                  <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkoutClientSecret }}>
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-sm shadow-sm overflow-hidden mb-6">
                  <div className="bg-slate-900 px-6 py-4 flex items-center gap-3 text-white">
                    <i className="fa-brands fa-stripe text-3xl"></i>
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-widest">Secure Checkout</h3>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Powered by Stripe</p>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-center border-b border-slate-200 pb-3 mb-3">
                      <span className="text-xs font-bold text-slate-700">Implementation & Setup Fee</span>
                      <span className="text-sm font-black text-slate-900">${SETUP_FEE.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between items-center border-b border-slate-200 pb-3 mb-3">
                      <div>
                        <span className="text-xs font-bold text-slate-700 block">Master Corporate Door License</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Recurring (MRR)</span>
                      </div>
                      <span className="text-sm font-black text-slate-900">${BASE_DOOR_FEE.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between items-center border-b border-slate-200 pb-3 mb-3">
                      <div>
                        <span className="text-xs font-bold text-slate-700 block">CheckHQ Location Processing Base</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Recurring (MRR)</span>
                      </div>
                      <span className="text-sm font-black text-slate-900">${CHECKHQ_FEE.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between items-center border-b border-slate-200 pb-3 mb-3">
                      <div>
                        <span className="text-xs font-bold text-slate-700 block">System Administrator Seat (1)</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Recurring (MRR)</span>
                      </div>
                      <span className="text-sm font-black text-slate-900">${SEAT_FEE.toFixed(2)}</span>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 p-4 mt-6 rounded-sm flex justify-between items-center">
                      <span className="text-xs font-black text-emerald-800 uppercase tracking-widest">Total Monthly Subscription</span>
                      <span className="text-2xl font-black text-emerald-600">${TOTAL_MRR.toFixed(2)}<span className="text-xs font-bold text-emerald-700 ml-1">/mo</span></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer Navigation */}
        <div className="bg-slate-100 border-t border-slate-200 p-6 flex justify-between items-center shrink-0">
          {currentStepIndex > 0 ? (
            <button 
              onClick={handleBack} 
              disabled={isSubmitting || uploadData.isUploading || step === 7 || step === 6} 
              className="px-6 py-2.5 text-[10px] font-black text-slate-600 uppercase tracking-widest border border-slate-300 bg-white hover:bg-slate-50 transition-colors rounded-sm disabled:opacity-50"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i> Back
            </button>
          ) : <div></div>}

          {step < 5 ? (
            <button 
              onClick={handleNext} 
              disabled={
                uploadData.isUploading ||
                (!is1099OrOwner && step === 1 && (!gearData.preferredName || !gearData.shirtSize || !gearData.jacketSize)) || 
                (step === 2 && ((!offlineDocs.w9 && (!financialData.ssn || (is1099OrOwner && !taxData.legalBusinessName))) || !financialData.routingNumber || !financialData.accountNumber)) || 
                (!is1099OrOwner && step === 3 && requiredAgreements.some(req => !complianceData[req])) ||
                (is1099OrOwner && step === 3 && !isComplianceSkipped && requiredAgreements.some(req => !complianceData[req] && !offlineDocs[req])) ||
                (is1099OrOwner && step === 4 && !uploadData.coiUrl)
              }
              className="px-8 py-2.5 text-[10px] font-black text-white uppercase tracking-widest bg-slate-900 hover:bg-slate-800 transition-colors rounded-sm shadow-sm disabled:opacity-50 flex items-center gap-2"
            >
              {uploadData.isUploading ? <><i className="fa-solid fa-spinner fa-spin"></i> Uploading...</> : <>Continue <i className="fa-solid fa-arrow-right"></i></>}
            </button>
          ) : step === 5 ? (
            <button 
              onClick={executeSwitchFlip} 
              disabled={isSubmitting || !signatureData.typedName}
              className="px-8 py-2.5 text-[10px] font-black text-white uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 transition-colors rounded-sm shadow-sm disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Executing Vault...</> : (
                isTenantOwner ? <>Sign & Proceed to Bank Link <i className="fa-solid fa-arrow-right"></i></> : <><i className="fa-solid fa-file-signature"></i> Execute & Enter Workspace</>
              )}
            </button>
          ) : step === 6 ? (
            // Handled natively by Stripe Embedded component's `onExit` callback
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Complete form above</div>
          ) : (
            // If the checkout isn't loaded yet, show the Authorize button. Otherwise, hide the footer button.
            !checkoutClientSecret && (
              <button 
                onClick={launchStripeCheckout} 
                disabled={isSubmitting}
                className="px-8 py-3 text-[10px] font-black text-white uppercase tracking-widest bg-indigo-600 hover:bg-indigo-500 transition-colors rounded-sm shadow-sm disabled:opacity-50 flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Loading Checkout...</> : <><i className="fa-brands fa-stripe"></i> Authorize & Launch Checkout</>}
              </button>
            )
          )}
        </div>

      </div>
    </div>
  );
}